import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { RowActions } from "@/components/RowActions";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, TrashIcon, PencilIcon, DownloadSimpleIcon, CopyIcon, CopySimpleIcon, PaletteIcon } from "@phosphor-icons/react";
import { exportToCSV } from "@/lib/csv";

import FormattedNumberInput from "@/components/FormattedNumberInput";
import ArticleImageGallery from "./masters/ArticleImageGallery";
const inp = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const selCls = "bg-[var(--bg-0)] border-[var(--border)] h-9 text-sm";
// Feb 2026 · Article Types are now sourced from the `article_types` Mongo
// collection (`GET /api/article-types/`) so admins can add / rename / retire
// short-codes without a code change. RNJ/RNW/RNK stay as the seeded defaults
// but the picker adapts to whatever is currently active.
const GRADES = ["I", "II", "III", "Special"];

const composeArticleNo = (a) => {
  const head = [
    a.article_type || "",
    a.width ? String(parseInt(a.width, 10)) : "",
    (a.article_no || "").trim(),
    (a.colour_code || "").trim(),
  ].filter(Boolean).join("/");
  const tail = [
    (a.description || "").trim().toUpperCase(),
    (a.colour || "").trim().toUpperCase(),
    normalizeSize(a.size).toUpperCase(),
    (a.grade || "").trim().toUpperCase(),
  ].filter(Boolean).join("-");
  if (head && tail) return `${head}-${tail}`;
  return head || tail;
};

const digitsOnly = (s) => (s || "").replace(/[^0-9]/g, "");
// Style No: alphanumeric-only, uppercase. Preserve dashes and slashes so
// customer refs like `VS-2024/BR-05` still round-trip cleanly.
const normalizeStyleNo = (s) => (s || "").toUpperCase().replace(/[^A-Z0-9\-\/ ]/g, "");
// Size: uppercase + auto-append `CM` when the operator types plain digits
// (e.g. `70` → `70CM`). Skips the append if the value already carries a
// recognised unit (CM/MM/INCH/IN/M/"). Runs on blur so intermediate keystrokes
// don't fight the user mid-type.
const normalizeSize = (raw) => {
  const s = (raw || "").toUpperCase().trim();
  if (!s) return "";
  const hasUnit = /(CM|MM|INCH|IN|M|")$/i.test(s);
  const endsDigit = /\d$/.test(s);
  if (endsDigit && !hasUnit) return `${s}CM`;
  return s;
};

const widthKey = (w) => (w ? String(parseInt(w, 10)) : "");

export default function ArticleMaster() {
  const [rows, setRows] = useState([]);
  // Feb 2026 · Table search — narrows the FG list by product code, article
  // no, style, description, colour or size. Runs entirely client-side so
  // the whole catalogue stays in memory (~1k rows fit easily).
  const [q, setQ] = useState("");
  // Feb 2026 · Dynamic article-types dropdown. Sourced from the Mongo
  // `article_types` collection so admins can extend the taxonomy in Masters
  // without any code changes here.
  const [articleTypes, setArticleTypes] = useState([]);
  // Colour Master lookup (Feb 2026) — loaded from /api/colors and merged
  // into `colourNameByCode` with MASTER-TAKES-PRIORITY so the swatch and
  // canonical spelling always win over what the articles catalogue happens
  // to have. Empty until the fetch resolves, so the article-driven fallback
  // still works during the first render.
  const [colourMaster, setColourMaster] = useState([]);
  // Feb 2026 · Smart Lock — usage snapshot for the currently-edited article.
  const [usage, setUsage] = useState({ linked: false, reasons: [] });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Base article shell + repeatable colour variants. On save (create-only) we
  // fan out one Article record per colour row so downstream joins (items,
  // orders, plans) keep working against a single-row schema. Edit mode still
  // operates on one row so we surface the singular colour fields directly.
  const empty = { article_type: "RNW", width: 0, article_no: "", style_no: "", description: "", size: "", grade: "I", colour_code: "", colour: "", hs_code: "", production_routing: "" };
  const [form, setForm] = useState(empty);
  const [colours, setColours] = useState([{ colour_code: "", colour: "" }]);
  // Sizes fanout (Feb 2026) — mirrors the colours flow so a single Article
  // No can spawn one record per (colour × size) combination. Empty rows are
  // ignored on save; if the entire list is empty we fall back to the base
  // Size field for backward compatibility with existing seed data.
  const [sizes, setSizes] = useState([{ size: "" }]);
  // Preview + selective-exclusion (Feb 2026) — a collapsible table below the
  // Colour/Size fanout that lists every composed Product Code before Save.
  // Operators uncheck combinations they don't stock; excluded keys are
  // filtered out of the fanout POST batch so nothing lands in the DB by
  // accident. Cleared whenever the dialog opens.
  const [excludedKeys, setExcludedKeys] = useState(new Set());
  const [previewOpen, setPreviewOpen] = useState(true);
  const [productionRoutings, setProductionRoutings] = useState([]);

  const load = async () => {
    const { data } = await api.get("/articles/");
    setRows(data);
    try {
      const { data: cm } = await api.get("/colors/");
      setColourMaster(Array.isArray(cm) ? cm : []);
    } catch {
      setColourMaster([]);
    }
    // Feb 2026 · Dynamic Article Types — powers the Type dropdown + backend
    // product-code composer. Falls back to the hardcoded triple only if the
    // fetch fails so a bad network doesn't wipe out the picker.
    try {
      const { data: at } = await api.get("/article-types/");
      const list = (Array.isArray(at) ? at : []).filter(t => t.is_active !== false);
      setArticleTypes(list);
      // If the current form's default type is empty or missing from the pool,
      // adopt the first available code so users don't stare at a blank picker.
      setForm(f => {
        if (f.article_type && list.some(t => t.code === f.article_type)) return f;
        return { ...f, article_type: list[0]?.code || "" };
      });
    } catch {
      setArticleTypes([]);
    }
    // Feb 2026 · Production Routings — 3 canonical paths (A · B · C)
    // exposed by /api/production-routing/paths.
    try {
      const { data: pr } = await api.get("/production-routing/paths");
      setProductionRoutings(Array.isArray(pr?.paths) ? pr.paths : []);
    } catch {
      setProductionRoutings([]);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Preview shows the FIRST colour row so the operator sees the article-number
  // template as they type. Additional colours re-use the same head with the
  // colour-code + colour swapped in.
  const preview = useMemo(
    () => composeArticleNo({ ...form, colour_code: (editing ? form.colour_code : colours[0]?.colour_code) || "", colour: (editing ? form.colour : colours[0]?.colour) || "" }),
    [form, colours, editing]
  );

  // "One article_no = one master identity" rule (Feb 2026): when the operator
  // types an article_no that already exists in the catalogue, we treat the
  // dialog as "add colour variant(s)" — the base fields (type / width /
  // description / size / grade) get auto-adopted from the existing row and
  // are locked so we don't fork the catalogue with divergent metadata under
  // the same numeric identity. The user can only enter new colour_code /
  // colour combinations. Server-side guard (`_check_article_no_base_match`)
  // enforces the same rule as a safety net.
  const existingForArticleNo = useMemo(() => {
    const an = (form.article_no || "").trim();
    if (!an) return [];
    return rows.filter(r => (r.article_no || "").trim() === an && (!editing || r.id !== editing.id));
  }, [rows, form.article_no, editing]);
  // Feb 2026 · Smart Lock — combine the existing "same article-number
  // family exists" lock with the new "referenced by SO/WO/BOM" lock. Both
  // keep identifier fields (category, width, article_no) frozen while
  // letting descriptive fields (colour, size, style) stay editable.
  const baseLocked = (!editing && existingForArticleNo.length > 0) || usage.linked;
  // Feb 2026 — same (article_no, colour_code) may now carry multiple SIZE
  // variants (e.g. 10656/01-70CM and 10656/01-77CM), so the uniqueness key
  // is `colour_code|size`, not colour_code alone. This drives the "already
  // exists" guard and the pre-flight duplicate check on the batch fanout.
  // FG-WV-2 · width is a variant attribute: key is width|colour_code|size and
  // colour-code suggestions are scoped to rows of the same width.
  const existingColourSizeKeys = useMemo(() => new Set(existingForArticleNo.map(r => `${widthKey(r.width)}|${(r.colour_code || "").trim()}|${normalizeSize(r.size || "")}`)), [existingForArticleNo]);
  const sameWidthRows = useMemo(() => {
    const wk = widthKey(form.width);
    return wk ? existingForArticleNo.filter(r => widthKey(r.width) === wk) : existingForArticleNo;
  }, [existingForArticleNo, form.width]);
  const existingColourCodes = useMemo(() => new Set(sameWidthRows.map(r => (r.colour_code || "").trim()).filter(Boolean)), [sameWidthRows]);

  // Suggest the next colour code — max existing numeric colour_code + 1, zero-padded
  // to match the width of the existing codes (typically 2 digits like "01", "02").
  const suggestedNextColourCode = useMemo(() => {
    if (existingColourCodes.size === 0) return "";
    const nums = Array.from(existingColourCodes).map(c => parseInt(c, 10)).filter(n => !isNaN(n));
    if (nums.length === 0) return "";
    const width = Math.max(...Array.from(existingColourCodes).map(c => c.length));
    return String(Math.max(...nums) + 1).padStart(Math.max(2, width), "0");
  }, [existingColourCodes]);

  // Colour-code → colour-name lookup (Feb 2026). Scans EVERY existing
  // article for `colour_code`, first-write-wins. When the operator types a
  // colour_code that's already used somewhere in the catalogue, we auto-fill
  // the colour description so buyers who reuse the same code across articles
  // (e.g. `01 = WHITE`) don't retype it. Preference order: match under the
  // current article_no first (most likely the correct swatch), then any
  // other article.
  const colourNameByCode = useMemo(() => {
    const m = new Map();
    // Pass 0: Colour Master takes precedence — canonical spelling wins.
    for (const c of colourMaster) {
      const cc = (c.code || "").trim();
      const cn = (c.name || "").trim();
      if (cc && cn) m.set(cc, cn);
    }
    const an = (form.article_no || "").trim();
    // Pass 1: rows sharing this article_no (only if Master missed).
    for (const r of rows) {
      if ((r.article_no || "").trim() !== an) continue;
      const cc = (r.colour_code || "").trim();
      const cn = (r.colour || "").trim();
      if (cc && cn && !m.has(cc)) m.set(cc, cn);
    }
    // Pass 2: fallback across the whole catalogue.
    for (const r of rows) {
      const cc = (r.colour_code || "").trim();
      const cn = (r.colour || "").trim();
      if (cc && cn && !m.has(cc)) m.set(cc, cn);
    }
    return m;
  }, [rows, form.article_no, colourMaster]);

  // Hex swatch lookup (Feb 2026): colour_code → `#RRGGBB` (only from Master —
  // legacy articles don't carry hex). Powers the article listing swatch dot.
  const colourHexByCode = useMemo(() => {
    const m = new Map();
    for (const c of colourMaster) {
      const cc = (c.code || "").trim();
      const hex = (c.hex || "").trim();
      if (cc && /^#[0-9a-fA-F]{6}$/.test(hex)) m.set(cc, hex);
    }
    return m;
  }, [colourMaster]);

  // Auto-adopt base fields when the entered article_no matches existing rows,
  // and pre-fill the first empty colour row with the suggested next code so the
  // operator only has to type the colour NAME.
  useEffect(() => {
    if (editing) return;
    if (existingForArticleNo.length === 0) return;
    const src = existingForArticleNo[0];
    setForm(f => ({
      ...f,
      article_type: src.article_type || f.article_type,
      width: f.width ? f.width : (src.width ?? f.width),
      // Style No is auto-adopted only when blank — once the operator has
      // typed a variant-specific reference (buyer may hand over a different
      // style number for one colour/size), we never clobber it, even if the
      // suggested colour code re-triggers this effect.
      style_no: f.style_no || src.style_no || "",
      description: src.description ?? f.description,
      grade: src.grade || f.grade,
      // Size is NOT auto-adopted — same (article_no, colour_code) can carry
      // multiple sizes (e.g. 70CM vs 77CM), so we keep the operator's typed
      // size and just default to the existing size when they haven't entered
      // anything themselves.
      size: f.size || src.size || "",
    }));
    // Pre-fill first empty colour_code slot with the suggested next value.
    if (suggestedNextColourCode) {
      setColours(cs => {
        if (cs.length === 0) return [{ colour_code: suggestedNextColourCode, colour: "" }];
        if (!(cs[0].colour_code || "").trim()) {
          const next = [...cs];
          next[0] = { ...next[0], colour_code: suggestedNextColourCode };
          return next;
        }
        return cs;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingForArticleNo.length, suggestedNextColourCode]);

  const openNew = () => { setEditing(null); setForm(empty); setColours([{ colour_code: "", colour: "" }]); setSizes([{ size: "" }]); setExcludedKeys(new Set()); setOpen(true); };
  const openEdit = async (r) => {
    setEditing(r);
    setForm({ ...empty, ...r });
    setColours([{ colour_code: r.colour_code || "", colour: r.colour || "" }]);
    setSizes([{ size: r.size || "" }]);
    setExcludedKeys(new Set());
    setUsage({ linked: false, reasons: [] });
    setOpen(true);
    try {
      const { data } = await api.get(`/master/usage/fg/${r.id}`);
      setUsage(data);
    } catch { /* usage best-effort */ }
  };
  // Duplicate → New Size (Feb 2026): opens the create dialog pre-filled from
  // an existing row with the SAME article_no + colour_code but a CLEARED
  // size field. Operator types a new size (e.g. 77CM) and hits Save — the
  // base-locked flow adopts everything else so the new variant slots
  // cleanly under the same product identity.
  const openDuplicateSize = (r) => {
    setEditing(null);
    setForm({
      ...empty,
      article_type: r.article_type || "RNW",
      width: r.width || 0,
      article_no: r.article_no || "",
      style_no: r.style_no || "",
      description: r.description || "",
      grade: r.grade || "I",
      size: "", // cleared — operator types new size
      colour_code: "",
      colour: "",
    });
    setColours([{ colour_code: r.colour_code || "", colour: r.colour || "" }]);
    setSizes([{ size: "" }]);
    setExcludedKeys(new Set());
    setOpen(true);
  };
  // Duplicate → New Colour (Feb 2026): mirror shortcut that KEEPS the size
  // and clears the colour_code + colour so buyers who order the same size
  // in multiple colours skip the retype. Suggested next colour code is
  // picked up automatically once the base-locked flow kicks in.
  const openDuplicateColour = (r) => {
    setEditing(null);
    setForm({
      ...empty,
      article_type: r.article_type || "RNW",
      width: r.width || 0,
      article_no: r.article_no || "",
      style_no: r.style_no || "",
      description: r.description || "",
      grade: r.grade || "I",
      size: r.size || "", // kept — same size, new colour
      colour_code: "",
      colour: "",
    });
    setColours([{ colour_code: "", colour: "" }]);
    setSizes([{ size: r.size || "" }]);
    setExcludedKeys(new Set());
    setOpen(true);
  };

  const addColour = () => setColours(c => [...c, { colour_code: "", colour: "" }]);
  const removeColour = (i) => setColours(c => c.length > 1 ? c.filter((_, idx) => idx !== i) : c);
  const updateColour = (i, key, value) => setColours(c => c.map((row, idx) => {
    if (idx !== i) return row;
    const next = { ...row, [key]: value };
    // Auto-fill colour name when the operator types a colour_code that
    // already lives in the catalogue AND the row's colour field is still
    // blank. We never overwrite text the operator has typed themselves.
    if (key === "colour_code" && (!row.colour || !row.colour.trim())) {
      const cc = (value || "").trim();
      const match = cc ? colourNameByCode.get(cc) : "";
      if (match) next.colour = match;
    }
    return next;
  }));

  const addSize = () => setSizes(s => [...s, { size: "" }]);
  const removeSize = (i) => setSizes(s => s.length > 1 ? s.filter((_, idx) => idx !== i) : s);
  const updateSize = (i, value) => setSizes(s => s.map((row, idx) => idx === i ? { ...row, size: value } : row));
  const blurSize = (i) => setSizes(s => s.map((row, idx) => idx === i ? { ...row, size: normalizeSize(row.size) } : row));

  // Live combos preview (create-only). Emits `{key, colour, size, code}`
  // rows so the Save flow and the preview table share one source of truth.
  const previewCombos = useMemo(() => {
    if (editing) return [];
    const validCols = colours.filter(c => (c.colour_code || "").trim() || (c.colour || "").trim());
    if (!validCols.length) return [];
    const sizeList = sizes.map(s => normalizeSize(s.size)).filter(Boolean);
    const sizeCombos = sizeList.length ? sizeList : [normalizeSize(form.size || "")];
    const normalizedBase = {
      ...form,
      description: (form.description || "").toUpperCase().trim(),
    };
    const out = [];
    for (const c of validCols) {
      for (const sz of sizeCombos) {
        const merged = { ...normalizedBase, colour_code: (c.colour_code || "").trim(), colour: (c.colour || "").trim(), size: sz || "" };
        const code = composeArticleNo(merged);
        const key = `${merged.colour_code}|${merged.colour}|${merged.size}`;
        out.push({ key, colour_code: merged.colour_code, colour: merged.colour, size: merged.size, code });
      }
    }
    return out;
  }, [colours, sizes, form, editing]);
  const includedCombos = useMemo(() => previewCombos.filter(p => !excludedKeys.has(p.key)), [previewCombos, excludedKeys]);
  const toggleExcluded = (key) => setExcludedKeys(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const save = async () => {
    if (!form.article_no) return toast.error("Article No is required");
    // Mar 2026 · HS Code Master Guard — pre-flight before hitting the API
    // so operators get instant field-level feedback. Backend still enforces
    // the same rule as the last line of defence.
    const typeHs = ((articleTypes || []).find(t => t.code === form.article_type)?.hs_code || "").trim();
    if (!(form.hs_code || "").trim() && !typeHs) {
      toast.error("HS Code is required — set one on this article or on the Article Type master.");
      try { document.querySelector('[data-testid="article-hs-code"]')?.focus(); } catch (_) {}
      return;
    }
    // Final normalization on save — catches values that were pasted or
    // never lost focus so what's persisted matches what the preview showed.
    const normalizedBase = {
      ...form,
      description: (form.description || "").toUpperCase().trim(),
      style_no: normalizeStyleNo(form.style_no || "").trim(),
      size: normalizeSize(form.size || ""),
    };
    try {
      if (editing) {
        // Single-row edit — check duplicates against every OTHER row.
        const composed = composeArticleNo(normalizedBase);
        const dup = rows.find(r => r.id !== editing.id && (r.product_code || "").toLowerCase() === composed.toLowerCase());
        if (dup) return toast.error(`Product Code "${composed}" already exists`);
        const payload = { ...normalizedBase, product_code: composed };
        await api.put(`/articles/${editing.id}`, payload);
        toast.success("Article updated");
      } else {
        // Fan-out driven by `previewCombos` so what the operator sees in the
        // preview table is exactly what lands in the DB. Excluded rows are
        // filtered out.
        if (!previewCombos.length) return toast.error("Add at least one Colour row");
        const combos = includedCombos;
        if (!combos.length) return toast.error("All variants are unchecked — tick at least one row to save");
        const payloads = combos.map(pc => {
          const merged = {
            ...normalizedBase,
            colour_code: pc.colour_code,
            colour: pc.colour,
            size: pc.size,
          };
          return { ...merged, product_code: pc.code };
        });
        // If article_no already exists, block only exact (colour_code+size)
        // pairs that duplicate an existing row. Same colour_code with a
        // different size is now allowed.
        if (existingForArticleNo.length > 0) {
          for (const p of payloads) {
            const cc = (p.colour_code || "").trim();
            const sz = normalizeSize(p.size || "");
            const key = `${widthKey(p.width)}|${cc}|${sz}`;
            if (cc && existingColourSizeKeys.has(key)) {
              return toast.error(`Colour "${cc}" with size "${sz || "—"}" already exists for Article No ${form.article_no} at width ${widthKey(p.width) || "—"} mm. Change the width, colour code or size.`);
            }
          }
        }
        const existingCodes = new Set(rows.map(r => (r.product_code || "").toLowerCase()));
        const seen = new Set();
        for (const p of payloads) {
          const key = (p.product_code || "").toLowerCase();
          if (existingCodes.has(key)) return toast.error(`Product Code "${p.product_code}" already exists`);
          if (seen.has(key)) return toast.error(`Two colour rows produce the same Product Code "${p.product_code}"`);
          seen.add(key);
        }

        const created = [];
        for (const p of payloads) {
          await api.post("/articles/", p);
          created.push(p.product_code);
        }
        toast.success(`Created ${created.length} article${created.length === 1 ? "" : "s"}`);
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const remove = async (id) => {
    // Feb 2026 · Smart Lock · block delete when the article is used elsewhere.
    try {
      const { data } = await api.get(`/master/usage/fg/${id}`);
      if (data.linked) {
        toast.error(`Cannot delete — used by ${data.reasons.join(", ")}. Deactivate it instead.`);
        return;
      }
    } catch { /* usage best-effort */ }
    if (!confirm("Delete this article?")) return;
    await api.delete(`/articles/${id}`);
    toast.success("Deleted"); load();
  };
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${text} copied`); } catch { /* ignore */ }
  };

  // Feb 2026 · FG Master search — narrows the article table by product
  // code / article no / style / description / colour / size. Client-side
  // so the whole catalogue stays in memory (~1k rows fit easily).
  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(r => [
      r.product_code, r.article_no, r.style_no, r.description,
      r.colour, r.colour_code, r.size, r.grade,
      r.article_type, r.hs_code, r.width,
    ].some(v => String(v || "").toLowerCase().includes(needle)));
  }, [rows, q]);

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-1">Finished Goods Coding</div>
          <h1 className="font-display text-3xl font-black tracking-tight">Finished Goods (FG) Master</h1>
          <p className="text-xs text-zinc-500 mt-1">Product Code auto-generated from Category · Width · Article No · Colour Code · Size · Grade</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="new-article-btn" onClick={openNew} className="h-10 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
            <PlusIcon size={14} weight="bold" />New Article
          </button>
          <button
            data-testid="reconcile-colours-btn"
            onClick={async () => {
              if (!window.confirm("Reconcile every article's Colour Name against the Colour Master?\n\nThis will rewrite the colour spelling on any article whose colour_code has a canonical entry in the master (e.g. 'Black' → 'BLACK'). Product Codes recompose automatically. Idempotent — safe to run more than once.")) return;
              try {
                const { data } = await api.post("/articles/reconcile-colours");
                if (data.updated === 0) {
                  toast.success(`All ${data.scanned} article${data.scanned === 1 ? "" : "s"} already canonical${data.missing_master ? ` · ${data.missing_master} skipped (no master entry)` : ""}`);
                } else {
                  toast.success(`Reconciled ${data.updated} of ${data.scanned} article${data.scanned === 1 ? "" : "s"}${data.missing_master ? ` · ${data.missing_master} skipped (no master)` : ""}`);
                }
                load();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Reconcile failed");
              }
            }}
            className="h-10 px-4 border border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-300 text-sm font-semibold rounded-sm flex items-center gap-2"
            title="Force every article's colour name to match the Colour Master's canonical spelling (eliminates BLACK/Black/BLK drift)"
          >
            <PaletteIcon size={14} weight="bold" />Reconcile Colours
          </button>
          <button
            onClick={() => exportToCSV("articles.csv", rows, [
              { key: "product_code", label: "Product Code" },
              { key: "article_type", label: "Category" },
              { key: "hs_code", label: "HS Code" },
              { key: "width", label: "Width" },
              { key: "article_no", label: "Article No" },
              { key: "style_no", label: "Style No" },
              { key: "colour_code", label: "Colour Code" },
              { key: "description", label: "Description" },
              { key: "colour", label: "Colour" },
              { key: "size", label: "Size" },
              { key: "grade", label: "Grade" },
            ])}
            className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm flex items-center gap-2"
          >
            <DownloadSimpleIcon size={14} weight="bold" />Export CSV
          </button>
        </div>
      </div>

      <div className="tactical-card overflow-x-auto">
        {/* Feb 2026 · Full-width search bar so users can find any FG SKU by
            product code, article no, style, description, colour or size.
            Live match summary appears once a query is typed. */}
        <div className="p-4 pb-3 border-b border-[var(--border)]">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--accent)] text-sm">⌕</span>
            <input
              data-testid="fg-master-search"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search Product Code · Article No · Style · Description · Colour · Size…"
              className="w-full h-10 pl-10 pr-24 bg-[var(--bg-0)] border-2 border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)] placeholder:text-zinc-500"
            />
            {q && (
              <button data-testid="fg-master-search-clear" onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 h-7 px-3 text-[10px] rounded-full border border-[var(--border)] hover:border-[var(--accent)] text-zinc-400 hover:text-white font-mono uppercase tracking-widest">
                Clear ✕
              </button>
            )}
          </div>
          {q && (
            <div className="mt-1 text-[10px] uppercase tracking-widest text-[var(--accent)] font-mono" data-testid="fg-master-search-summary">
              · {filteredRows.length} of {rows.length} article{filteredRows.length === 1 ? "" : "s"} match "{q}"
            </div>
          )}
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-5 py-3 font-medium">Product ID</th>
              <th className="text-left px-5 py-3 font-medium">Product Code</th>
              <th className="text-left px-5 py-3 font-medium">Category</th>
              <th className="text-left px-5 py-3 font-medium">HS Code</th>
              <th className="text-right px-5 py-3 font-medium">Width</th>
              <th className="text-right px-5 py-3 font-medium">Article No</th>
              <th className="text-left px-5 py-3 font-medium">Style No</th>
              <th className="text-right px-5 py-3 font-medium">Colour Code</th>
              <th className="text-left px-5 py-3 font-medium">Description</th>
              <th className="text-left px-5 py-3 font-medium">Colour</th>
              <th className="text-left px-5 py-3 font-medium">Size</th>
              <th className="text-left px-5 py-3 font-medium">Grade</th>
              <th className="text-right px-5 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map(r => (
              <tr key={r.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40" data-testid={`article-${r.product_code}`}>
                <td className="px-5 py-3 font-mono text-emerald-300 whitespace-nowrap" data-testid={`article-product-id-${r.product_id || r.id.slice(0,8)}`} title={r.identity_status === "ACTION_REQUIRED" ? `Action Required — missing ${(r.identity_missing || []).join(", ")}` : "Locked Product ID — never changes even if Description / Colour / Grade is edited"}>
                  {r.identity_status === "ACTION_REQUIRED" ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-red-500/15 border border-red-500/40 text-red-300 text-[10px] font-mono uppercase tracking-widest" data-testid={`identity-action-required-${r.id.slice(0,8)}`}>
                      ⚠ Action Required
                    </span>
                  ) : (
                    r.product_id || <span className="text-zinc-600">—</span>
                  )}
                </td>
                <td className="px-5 py-3">
                  <button onClick={() => copy(r.product_code)} className="font-mono font-semibold text-[var(--accent)] hover:underline flex items-center gap-1.5" title="Click to copy">
                    {r.product_code}
                    <CopyIcon size={11} className="opacity-50" />
                  </button>
                </td>
                <td className="px-5 py-3 font-mono font-semibold">{r.article_type}</td>
                <td className="px-5 py-3 font-mono text-zinc-300">{r.hs_code || <span className="text-zinc-600">—</span>}</td>
                <td className="px-5 py-3 text-right font-mono">{r.width}</td>
                <td className="px-5 py-3 text-right font-mono">{r.article_no || r.article}</td>
                <td className="px-5 py-3 font-mono text-zinc-300">{r.style_no || <span className="text-zinc-600">—</span>}</td>
                <td className="px-5 py-3 text-right font-mono">
                  <span className="inline-flex items-center gap-2 justify-end">
                    {colourHexByCode.get(r.colour_code) && (
                      <span
                        data-testid={`article-swatch-${r.product_code}`}
                        className="inline-block w-3 h-3 rounded-full border border-[var(--border)] ring-1 ring-black/20"
                        style={{ background: colourHexByCode.get(r.colour_code) }}
                        title={colourHexByCode.get(r.colour_code)}
                      />
                    )}
                    <span>{r.colour_code}</span>
                  </span>
                </td>
                <td className="px-5 py-3 text-zinc-400">{r.description}</td>
                <td className="px-5 py-3">{r.colour}</td>
                <td className="px-5 py-3 font-mono">{r.size}</td>
                <td className="px-5 py-3">
                  <span className={`px-2 py-0.5 rounded-sm text-xs font-mono font-semibold ${r.grade === "I" ? "bg-emerald-500/15 text-emerald-400" : r.grade === "II" ? "bg-amber-500/15 text-amber-400" : "bg-zinc-500/15 text-zinc-400"}`}>{r.grade}</span>
                </td>
                <td className="px-5 py-3 text-right">
                  <RowActions
                    testid={`article-actions-${r.product_code}`}
                    items={[
                      { label: "Duplicate → New Size", tone: "approve", testid: `dup-size-${r.product_code}`, onClick: () => openDuplicateSize(r) },
                      { label: "Duplicate → New Colour", tone: "warn", testid: `dup-colour-${r.product_code}`, onClick: () => openDuplicateColour(r) },
                      { label: "Edit", testid: `article-edit-${r.product_code}`, onClick: () => openEdit(r) },
                      { label: "Delete", tone: "danger", testid: `article-delete-${r.product_code}`, onClick: () => remove(r.id) },
                    ]}
                  />
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={13} className="text-center py-16 text-zinc-500">No articles yet — click New Article to create your first product code.</td></tr>}
            {rows.length > 0 && filteredRows.length === 0 && <tr><td colSpan={13} className="text-center py-16 text-zinc-500">No articles match "{q}"</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="article-dialog" description="Add or edit a finished-good article with images, HSN code and BOM link.">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "New"} Article</DialogTitle></DialogHeader>

          {/* Feb 2026 · Digital Catalogue image gallery — only surfaced in
              edit mode so we always have an article_id to upload against.
              First image auto-becomes Primary; drag to reorder. */}
          {editing && (
            <div className="mb-4 pb-4 border-b border-[var(--border)]">
              <div className="overline mb-2">Product Images <span className="normal-case text-zinc-500 ml-1">· up to 10 · JPG / PNG / WEBP · 5MB max</span></div>
              <ArticleImageGallery
                articleId={editing.id}
                images={form.images || []}
                onChange={(imgs) => setForm(f => ({ ...f, images: imgs }))}
                canEdit={true}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <F label="Category">
              <Select value={form.article_type} onValueChange={v => {
                // Feb 2026 · Auto-inherit HS Code from the picked article type
                // so Export docs pre-fill. Only overwrite when the current HS
                // field is empty OR still equal to the previously-picked type's
                // HS — never clobber a manual edit.
                const prev = articleTypes.find(t => t.code === form.article_type);
                const next = articleTypes.find(t => t.code === v);
                setForm(f => {
                  const nextHs = next?.hs_code || "";
                  const wasInherited = !f.hs_code || f.hs_code === (prev?.hs_code || "");
                  return {
                    ...f,
                    article_type: v,
                    hs_code: wasInherited ? nextHs : f.hs_code,
                  };
                });
              }} disabled={baseLocked}>
                <SelectTrigger data-testid="article-type" className={selCls}><SelectValue placeholder="Pick a type…" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {articleTypes.length === 0 && <div className="px-3 py-4 text-xs text-zinc-500 italic">No active categories — add some in Masters → Product Category.</div>}
                  {articleTypes.map(t => (
                    <SelectItem key={t.id || t.code} value={t.code} data-testid={`article-type-opt-${t.code}`}>
                      <span className="font-mono font-semibold mr-1.5">{t.code}</span>
                      <span className="text-zinc-400">· {t.name}</span>
                      {t.hs_code && <span className="text-zinc-500 text-[10px] ml-1.5 font-mono">· HS {t.hs_code}</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
            <F label="Width (mm)"><FormattedNumberInput data-testid="article-width" value={form.width} onChange={e => setForm({ ...form, width: parseFloat(e.target.value) || 0 })} className={inp} placeholder="25" disabled={(!!editing && !!editing.product_id) || usage.linked} /></F>
            <F label="Article No (numeric)">
              <input
                data-testid="article-no"
                inputMode="numeric"
                value={form.article_no}
                onChange={e => setForm({ ...form, article_no: digitsOnly(e.target.value) })}
                className={inp}
                placeholder="001"
              />
            </F>
            {/* Mar 2026 · HS Code Master Guard — required for every FG.
                Auto-inherits from the Article Type; user can override
                but cannot leave it blank on Save. */}
            <F label="HS Code * (auto from Category · editable · required)">
              <input
                data-testid="article-hs-code"
                data-nouppercase
                value={form.hs_code}
                onChange={e => setForm({ ...form, hs_code: e.target.value.replace(/[^0-9A-Za-z]/g, "") })}
                className={inp + (!(form.hs_code || "").trim() ? " border-red-500/60" : "")}
                placeholder="e.g. 58063200 · required"
              />
              {!(form.hs_code || "").trim() && (
                <div className="text-[10px] font-mono text-red-400 mt-1">HS Code cannot be blank. Set it here or on the Article Type master.</div>
              )}
            </F>
            <F label="Size (default — override per row below)"><input data-testid="article-size" value={form.size} onChange={e => setForm({ ...form, size: e.target.value.toUpperCase() })} onBlur={e => setForm({ ...form, size: normalizeSize(e.target.value) })} className={inp} placeholder="70 → 70CM · M / L / XL" /></F>
            <F label={baseLocked ? "Style No (customer reference — editable, buyer may change per variant)" : "Style No (customer reference)"} className="col-span-2">
              <input
                data-testid="article-style-no"
                value={form.style_no}
                onChange={e => setForm({ ...form, style_no: normalizeStyleNo(e.target.value) })}
                className={inp}
                placeholder="e.g. VS-2024-BR-05 (uppercase alphanumeric)"
              />
            </F>
            <F label="Description" className="col-span-2">
              <input data-testid="article-desc" value={form.description} onChange={e => setForm({ ...form, description: e.target.value.toUpperCase() })} className={inp} placeholder="E.G. LADIES UNDERWEAR ELASTIC BAND" disabled={baseLocked} />
            </F>
            <F label="Grade" className="col-span-2"><input data-testid="article-grade" value={form.grade} onChange={e => setForm({ ...form, grade: e.target.value.toUpperCase() })} className={inp} placeholder="I / II / III / SPECIAL" disabled={baseLocked} /></F>
            {/* Feb 2026 · Production Routing · one of the 3 canonical
                paths that pins the departmental sequence for any WO
                spawned off this article. BOM Explode uses this to
                suggest Dyed (A/B) vs Grey (C) yarn RMs. */}
            <F label="Production Routing" className="col-span-2">
              <Select value={form.production_routing || undefined} onValueChange={v => setForm({ ...form, production_routing: v })}>
                <SelectTrigger data-testid="article-production-routing" className={selCls}>
                  <SelectValue placeholder="Pick a routing… (Path A · B · C · D)" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {productionRoutings.map(p => (
                    <SelectItem key={p.code} value={p.code} data-testid={`article-routing-opt-${p.code}`}>
                      <span className="font-mono text-xs text-[var(--accent)]">{p.label}</span>
                      <span className="text-[10px] text-zinc-500 ml-2">· {p.description}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.production_routing && (
                <div className="mt-1 text-[10px] text-zinc-500 font-mono">
                  Yarn hint: <span className={`ml-1 px-1.5 py-0.5 rounded-sm border ${
                    (productionRoutings.find(p => p.code === form.production_routing)?.yarn_kind_hint) === "GREY"
                      ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
                      : "border-fuchsia-500/40 text-fuchsia-300 bg-fuchsia-500/10"
                  }`}>
                    {productionRoutings.find(p => p.code === form.production_routing)?.yarn_kind_hint || "—"}
                  </span>
                </div>
              )}
            </F>
          </div>

          {baseLocked && (
            <div className="mt-3 rounded-sm border border-amber-500/40 bg-amber-500/10 overflow-hidden" data-testid="article-base-locked-banner">
              <div className="px-3 py-2 flex items-start gap-2 border-b border-amber-500/20">
                <span className="text-amber-400 mt-0.5">●</span>
                <div className="text-xs flex-1">
                  <span className="font-semibold text-amber-400">Article No {form.article_no} already exists</span>
                  <span className="text-zinc-400"> — {existingForArticleNo.length} colour/size variant{existingForArticleNo.length === 1 ? "" : "s"} generated so far.</span>
                  <div className="text-zinc-500 mt-1">Type, Description and Grade are shared. Add a new width, colour code, size, or both — same width+colour+size combos are the only blocker{suggestedNextColourCode ? <>. Suggested next code: <span className="font-mono text-emerald-400 font-semibold" data-testid="suggested-next-cc">{suggestedNextColourCode}</span></> : null}.</div>
                </div>
              </div>
              <div className="max-h-40 overflow-y-auto">
                <table className="w-full text-xs" data-testid="existing-variants-table">
                  <thead className="sticky top-0 bg-[var(--bg-2)]/80 backdrop-blur">
                    <tr className="uppercase tracking-widest text-[10px] text-zinc-500">
                      <th className="text-left px-3 py-1.5 w-16">Code</th>
                      <th className="text-left px-3 py-1.5">Colour</th>
                      <th className="text-left px-3 py-1.5 w-20">Size</th>
                      <th className="text-left px-3 py-1.5">Product Code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingForArticleNo.map(v => (
                      <tr key={v.id} className="border-t border-amber-500/10" data-testid={`existing-variant-${v.colour_code || v.id}`}>
                        <td className="px-3 py-1.5 font-mono font-semibold text-amber-300">{v.colour_code || "—"}</td>
                        <td className="px-3 py-1.5">{v.colour || <span className="text-zinc-600">—</span>}</td>
                        <td className="px-3 py-1.5 font-mono">{v.size || <span className="text-zinc-600">—</span>}</td>
                        <td className="px-3 py-1.5 font-mono text-[11px] text-zinc-400 truncate">{v.product_code}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Colour variants — Type/Width/Article No stay locked, only colour rows fan out.
              Edit mode collapses to a single row so we're editing one record cleanly. */}
          <div className="mt-4 rounded-sm border border-[var(--border)]" data-testid="colour-variants-table">
            <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border)] bg-[var(--bg-2)]/40">
              <div className="overline">Colour Variants {!editing && <span className="normal-case text-zinc-500 ml-1">· one Article record per row on save</span>}</div>
              {!editing && (
                <button data-testid="add-colour-row" onClick={addColour} className="h-7 px-2.5 border border-[var(--border)] hover:bg-[var(--bg-1)] text-xs rounded-sm flex items-center gap-1.5">
                  <PlusIcon size={11} weight="bold" />Add Colour
                </button>
              )}
            </div>
            {editing ? (
              <div className="grid grid-cols-2 gap-3 p-3">
                <F label="Colour Code (numeric)">
                  <input
                    data-testid="article-colour-code"
                    inputMode="numeric"
                    value={form.colour_code}
                    onChange={e => {
                      const cc = digitsOnly(e.target.value);
                      const patch = { colour_code: cc };
                      // Auto-fill colour name from catalogue when field is blank.
                      if (cc && (!form.colour || !form.colour.trim())) {
                        const match = colourNameByCode.get(cc);
                        if (match) patch.colour = match;
                      }
                      setForm({ ...form, ...patch });
                    }}
                    className={inp}
                    placeholder="005"
                  />
                </F>
                <F label="Colour">
                  <input data-testid="article-colour" value={form.colour} onChange={e => setForm({ ...form, colour: e.target.value })} className={inp} placeholder="White / Black" />
                </F>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                    <th className="text-left px-3 py-1.5 font-medium w-8">#</th>
                    <th className="text-left px-3 py-1.5 font-medium w-40">Colour Code</th>
                    <th className="text-left px-3 py-1.5 font-medium">Colour Description</th>
                    <th className="text-left px-3 py-1.5 font-medium">Preview</th>
                    <th className="text-right px-3 py-1.5 font-medium w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {colours.map((c, i) => {
                    const preview_i = composeArticleNo({ ...form, colour_code: c.colour_code, colour: c.colour });
                    return (
                      <tr key={i} className="border-b border-[var(--border)]/60" data-testid={`colour-row-${i}`}>
                        <td className="px-3 py-2 font-mono text-zinc-500 text-xs">{i + 1}</td>
                        <td className="px-3 py-2">
                          <input data-testid={`colour-code-${i}`} inputMode="numeric" value={c.colour_code} onChange={e => updateColour(i, "colour_code", digitsOnly(e.target.value))} className={inp} placeholder="005" />
                        </td>
                        <td className="px-3 py-2">
                          <input data-testid={`colour-desc-${i}`} value={c.colour} onChange={e => updateColour(i, "colour", e.target.value)} className={inp} placeholder="White / Black" />
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-mono text-[10px] text-[var(--accent)] truncate max-w-[240px]" title={preview_i}>{preview_i || <span className="text-zinc-600 italic normal-case">—</span>}</div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => removeColour(i)} disabled={colours.length === 1} className="p-1 rounded-sm hover:bg-red-500/10 text-zinc-400 hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed" title="Remove row">
                            <TrashIcon size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {!editing && (
            <div className="mt-3 rounded-sm border border-[var(--border)]" data-testid="size-variants-table">
              <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border)] bg-[var(--bg-2)]/40">
                <div className="overline">Size Variants <span className="normal-case text-zinc-500 ml-1">· cartesian × Colour Variants · empty = use default size above</span></div>
                <button data-testid="add-size-row" onClick={addSize} className="h-7 px-2.5 border border-[var(--border)] hover:bg-[var(--bg-1)] text-xs rounded-sm flex items-center gap-1.5">
                  <PlusIcon size={11} weight="bold" />Add Size
                </button>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                    <th className="text-left px-3 py-1.5 font-medium w-8">#</th>
                    <th className="text-left px-3 py-1.5 font-medium">Size</th>
                    <th className="text-right px-3 py-1.5 font-medium w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {sizes.map((s, i) => (
                    <tr key={i} className="border-b border-[var(--border)]/60" data-testid={`size-row-${i}`}>
                      <td className="px-3 py-2 font-mono text-zinc-500 text-xs">{i + 1}</td>
                      <td className="px-3 py-2">
                        <input
                          data-testid={`size-value-${i}`}
                          value={s.size}
                          onChange={e => updateSize(i, e.target.value.toUpperCase())}
                          onBlur={() => blurSize(i)}
                          className={inp}
                          placeholder="70 → 70CM · M / L / XL"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => removeSize(i)} disabled={sizes.length === 1} className="p-1 rounded-sm hover:bg-red-500/10 text-zinc-400 hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed" title="Remove row">
                          <TrashIcon size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!editing && previewCombos.length > 1 && (
            <div className="mt-3 rounded-sm border border-[var(--border)]" data-testid="combos-preview-panel">
              <button
                type="button"
                onClick={() => setPreviewOpen(o => !o)}
                data-testid="toggle-combos-preview"
                className="w-full flex items-center justify-between px-3 py-2 bg-[var(--bg-2)]/40 border-b border-[var(--border)] text-left"
              >
                <div className="overline">
                  Combinations Preview <span className="normal-case text-zinc-500 ml-1">· {includedCombos.length} of {previewCombos.length} selected</span>
                </div>
                <div className="text-xs text-zinc-400 flex items-center gap-3">
                  {excludedKeys.size > 0 && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); setExcludedKeys(new Set()); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setExcludedKeys(new Set()); } }}
                      data-testid="combos-clear-exclusions"
                      className="text-emerald-400 hover:underline cursor-pointer"
                    >Select all</span>
                  )}
                  <span>{previewOpen ? "▾ Hide" : "▸ Show"}</span>
                </div>
              </button>
              {previewOpen && (
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm" data-testid="combos-preview-table">
                    <thead className="sticky top-0 bg-[var(--bg-1)] z-10">
                      <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                        <th className="text-left px-3 py-1.5 font-medium w-10">
                          <input
                            type="checkbox"
                            data-testid="combos-toggle-all"
                            checked={includedCombos.length === previewCombos.length}
                            ref={el => { if (el) el.indeterminate = includedCombos.length > 0 && includedCombos.length < previewCombos.length; }}
                            onChange={e => setExcludedKeys(e.target.checked ? new Set() : new Set(previewCombos.map(p => p.key)))}
                            className="cursor-pointer"
                          />
                        </th>
                        <th className="text-left px-3 py-1.5 font-medium w-8">#</th>
                        <th className="text-left px-3 py-1.5 font-medium">Colour</th>
                        <th className="text-left px-3 py-1.5 font-medium">Size</th>
                        <th className="text-left px-3 py-1.5 font-medium">Product Code</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewCombos.map((p, idx) => {
                        const excluded = excludedKeys.has(p.key);
                        return (
                          <tr key={p.key} data-testid={`combo-row-${idx}`} className={`border-b border-[var(--border)]/60 hover:bg-[var(--bg-2)]/40 ${excluded ? "opacity-40" : ""}`}>
                            <td className="px-3 py-1.5">
                              <input
                                type="checkbox"
                                data-testid={`combo-toggle-${idx}`}
                                checked={!excluded}
                                onChange={() => toggleExcluded(p.key)}
                                className="cursor-pointer"
                              />
                            </td>
                            <td className="px-3 py-1.5 font-mono text-zinc-500 text-xs">{idx + 1}</td>
                            <td className="px-3 py-1.5 text-xs">
                              <span className="font-mono text-amber-300">{p.colour_code || "—"}</span>
                              <span className="ml-1 text-zinc-300">{p.colour || <span className="text-zinc-600">—</span>}</span>
                            </td>
                            <td className="px-3 py-1.5 font-mono text-xs">{p.size || <span className="text-zinc-600">—</span>}</td>
                            <td className="px-3 py-1.5 font-mono text-[11px] text-[var(--accent)] truncate max-w-[420px]" title={p.code}>{p.code || <span className="text-zinc-600 italic normal-case">fill fields to preview</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="mt-4 p-4 rounded-sm bg-[var(--bg-2)] border border-[var(--accent)]/30" data-testid="article-preview">
            <div className="overline mb-1">Product Code{!editing && includedCombos.length > 1 && <span className="normal-case text-zinc-500 ml-1">· {includedCombos.length} variants will be created{excludedKeys.size > 0 ? ` (${excludedKeys.size} excluded)` : ""}</span>}</div>
            <div className="flex items-center gap-3">
              <div className="font-mono text-2xl font-bold tracking-tight text-[var(--accent)] flex-1">
                {preview || <span className="text-zinc-500 text-base font-normal italic">Fill fields to preview</span>}
              </div>
              {preview && (
                <button onClick={() => copy(preview)} className="p-2 rounded-sm border border-[var(--border)] hover:bg-[var(--bg-1)]" title="Copy">
                  <CopyIcon size={14} />
                </button>
              )}
            </div>
            <div className="text-[10px] text-zinc-500 mt-2 font-mono tracking-wider">
              CATEGORY / WIDTH / ARTICLE NO / COLOUR CODE - DESCRIPTION - COLOUR - SIZE - GRADE
            </div>
          </div>

          {/* Feb 2026 · Smart Lock banner — appears when the article is
              referenced by any downstream doc (SO/ESO/WO/BOM). Identifier
              fields lock automatically; descriptive fields stay editable. */}
          {usage.linked && (
            <div className="rounded-sm border border-amber-500/50 bg-amber-500/5 p-2 mb-3 text-[11px] text-amber-200">
              <b>Locked identifier fields</b> — this article is referenced by {usage.reasons.join(", ")}. Category / width / article no / HS code stay read-only; description, colour, size and style remain editable.
            </div>
          )}

          {/* Feb 2026 · Active toggle · retires the FG from NEW SO / WO / BOM
              dropdowns while preserving every historical reference. */}
          <div className="rounded-sm border border-[var(--border)] bg-[var(--bg-0)] p-2 mb-3 flex items-center justify-between">
            <div>
              <div className="overline text-[10px]">Lifecycle</div>
              <div className="text-xs text-zinc-400">Inactive articles are hidden from new SO / BOM / WO pickers · historical data preserved</div>
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input
                data-testid="fg-is-active"
                type="checkbox"
                checked={form.is_active !== false}
                onChange={e => setForm({ ...form, is_active: e.target.checked })}
              />
              <span className={form.is_active !== false ? "text-emerald-300 font-mono uppercase tracking-widest" : "text-zinc-500 font-mono uppercase tracking-widest"}>
                {form.is_active !== false ? "Active" : "Inactive"}
              </span>
            </label>
          </div>

          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button data-testid="save-article" onClick={save} className="px-4 py-2 rounded-sm bg-[var(--accent)] text-sm font-semibold text-white">Save Article</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children, className = "" }) {
  return <div className={className}><div className="overline mb-1.5">{label}</div>{children}</div>;
}
